import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';

// Google brand mark. Sourced from the same SVG used on RegisterPage.
const GoogleGlyph = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.12A6.98 6.98 0 0 1 5.5 12c0-.74.13-1.45.34-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.77.43 3.44 1.18 4.96l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.65l3.15-3.15C17.45 2.11 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.29 9.14 5.38 12 5.38z" />
  </svg>
);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || null;

  // Detect if the sign-in target is an admin route (via ?next=/admin* or router state).
  // In that case, hide the "Create account" link (admin is invite-only) and show
  // a helper card.
  const nextParam = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get('next') || from || '';
  }, [location.search, from]);
  const isAdminLogin = /^\/admin(\/|$)/.test(nextParam);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!email || !password) {
      setLocalError('Please fill in all fields');
      return;
    }

    const result = await login({ email, password });

    if (result.success) {
      // Route by role/state:
      //   admin / super_admin  -> /admin
      //   incomplete profile   -> /onboarding
      //   otherwise            -> "from" or homepage
      const u = result.user || {};
      let dest = from;
      if (u.role === 'check_in_staff') dest = '/check-in';
      else if (['admin', 'super_admin'].includes(u.role)) dest = '/admin';
      else if (!u.profile_complete) dest = '/onboarding';
      else if (!dest) dest = '/';
      navigate(dest, { replace: true });
    } else {
      setLocalError(result.error);
    }
  };

  // Bundle 54 - magic-link sign-in option. Users who signed up via
  // Google or a magic-link invite may not have a password set; this
  // gives them a first-class path back into their account without
  // needing to "reset" a password they never chose.
  const sendMagicLink = async () => {
    setLocalError('');
    if (!email) {
      setLocalError('Enter your email to receive a sign-in link.');
      return;
    }
    setMagicSending(true);
    try {
      const redirect = window.location.origin + (from || '/');
      await api.post('/auth/magic-link', { email, redirect });
      setMagicSent(true);
      toast.success('Check your inbox for the sign-in link.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLocalError(typeof detail === 'string' ? detail : 'Could not send sign-in link.');
    } finally {
      setMagicSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="font-display text-3xl font-bold text-primary-900 mb-2">
          {isAdminLogin ? 'Admin sign in' : 'Welcome back'}
        </h1>
        <p className="text-neutral-600">
          {isAdminLogin ? 'Sign in with your admin credentials.' : 'Sign in to your Dostination account.'}
        </p>
      </div>

      {isAdminLogin && (
        <div className="flex items-start gap-2 text-xs text-secondary-800 bg-secondary-50/70 border border-secondary-200 rounded-lg p-3">
          <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>Admin access is invite-only. Please contact your organizer if you need access.</span>
        </div>
      )}

      {localError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{localError}</AlertDescription>
        </Alert>
      )}

      {!isAdminLogin && (
        <div className="space-y-3" data-testid="google-signin-block">
          <button
            type="button"
            onClick={() => {
              // Emergent-managed Google Auth. DO NOT hardcode redirect URL; the
              // handler in AuthCallback.jsx picks up the session_id from the
              // returned hash and exchanges it for our session cookie.
              const redirectUrl = window.location.href;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            data-testid="login-google-btn"
            className="w-full flex items-center justify-center gap-3 rounded-xl border border-secondary-300 bg-gradient-to-r from-white to-secondary-50 px-4 py-3 text-primary-900 font-semibold text-sm shadow-sm hover:shadow-gold hover:from-secondary-50 hover:to-secondary-100 transition-all"
          >
            <GoogleGlyph className="h-5 w-5" />
            <span>Continue with Google</span>
          </button>
          <div className="relative flex items-center py-1">
            <div className="flex-grow border-t border-neutral-200" />
            <span className="mx-3 text-[11px] uppercase tracking-widest text-neutral-500">or sign in with email</span>
            <div className="flex-grow border-t border-neutral-200" />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10"
              disabled={loading}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/auth/forgot-password"
              className="text-sm text-secondary-500 hover:text-secondary-600 hover:underline"
            >
              Forgot?
            </Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          size="lg"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </Button>
      </form>

      {/* Bundle 54 - magic-link sign-in third option */}
      {!isAdminLogin && (
        <div className="pt-1" data-testid="magic-link-block">
          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-neutral-200" />
            <span className="mx-3 text-[11px] uppercase tracking-widest text-neutral-500">or</span>
            <div className="flex-grow border-t border-neutral-200" />
          </div>
          {magicSent ? (
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
              data-testid="magic-link-sent"
            >
              We sent a sign-in link to <strong>{email}</strong>. The link expires in 15 minutes.
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={sendMagicLink}
              disabled={magicSending}
              data-testid="login-magic-link-btn"
              className="w-full border-neutral-300 hover:border-secondary-400"
            >
              {magicSending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Email me a sign-in link
                </>
              )}
            </Button>
          )}
          <p className="mt-2 text-[11px] text-neutral-500 text-center">
            Best if you signed up with Google or via an invite and never chose a password.
          </p>
        </div>
      )}

      {!isAdminLogin && (
        <div className="text-center text-sm text-neutral-600">
          Don&apos;t have an account?{' '}
          <Link
            to="/auth/register"
            className="text-secondary-500 font-semibold hover:text-secondary-600 hover:underline"
          >
            Create one
          </Link>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
